class SimpleSlider {
  constructor(selector, options = {}) {
    this.container =
      typeof selector === 'string'
        ? document.querySelector(selector)
        : selector;
    this.options = {
      perPage: options.perPage || 2,
      perMove: options.perMove || 1,
      gap: options.gap || '2rem',
      autoplay: options.autoplay || false,
      autoplaySpeed: options.autoplaySpeed || 3000,
      loop: options.loop || false,
      pagination: options.pagination !== false,
      arrows: options.arrows !== false,
      ...options,
    };

    this.currentSlide = 0;
    this.totalSlides = 0;
    this.autoplayInterval = null;
    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;

    this.init();
  }

  init() {
    if (!this.container) return;

    this.track = this.container.querySelector('.splide__track');
    this.list = this.container.querySelector('.splide__list');
    this.slides = this.container.querySelectorAll('.splide__slide');
    this.totalSlides = this.slides.length;

    if (this.totalSlides === 0) return;

    this.setupStyles();
    this.setupArrows();
    this.setupPagination();
    this.setupTouch();
    this.setupKeyboard();
    this.setupImageModal();
    this.goToSlide(0);

    window.addEventListener('resize', () => {
      this.setupTouch();
      this.updateArrows();
    });

    if (this.options.autoplay) {
      this.startAutoplay();
    }
  }

  setupStyles() {
    this.slides.forEach((slide) => {
      slide.style.removeProperty('flex');
      slide.style.removeProperty('height');
    });

    const totalSlides = this.totalSlides;
    this.list.style.width = `${totalSlides * 100}%`;
  }

  setupArrows() {
    if (!this.options.arrows) return;

    const prevBtn = this.container.querySelector('.splide__arrow--prev');
    const nextBtn = this.container.querySelector('.splide__arrow--next');

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        this.stopAutoplay();
        this.prev();
        this.startAutoplay();
        setTimeout(() => this.startAutoplay(), 5000);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        this.stopAutoplay();
        this.next();
        this.startAutoplay();
        setTimeout(() => this.startAutoplay(), 5000);
      });
    }

    this.updateArrows();
  }

  setupPagination() {
    return;
  }

  setupTouch() {
    const isMobile = window.innerWidth < 1024;
    if (!isMobile || !this.track) return;

    this.track.addEventListener('touchstart', (e) => this.handleTouchStart(e));
    this.track.addEventListener('touchmove', (e) => this.handleTouchMove(e));
    this.track.addEventListener('touchend', (e) => this.handleTouchEnd(e));

    this.track.addEventListener('mousedown', (e) => this.handleMouseDown(e));
    this.track.addEventListener('mousemove', (e) => this.handleMouseMove(e));
    this.track.addEventListener('mouseup', (e) => this.handleMouseUp(e));
    this.track.addEventListener('mouseleave', (e) => this.handleMouseLeave(e));
  }

  handleTouchStart(e) {
    this.startX = e.touches[0].clientX;
    this.isDragging = true;
  }

  handleTouchMove(e) {
    if (!this.isDragging) return;
    e.preventDefault();
    this.currentX = e.touches[0].clientX;
  }

  handleTouchEnd(e) {
    if (!this.isDragging) return;

    const diff = this.startX - this.currentX;
    const threshold = 50;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        this.next();
      } else {
        this.prev();
      }
    }

    this.isDragging = false;
  }

  handleMouseDown(e) {
    this.startX = e.clientX;
    this.isDragging = true;
    this.track.style.cursor = 'grabbing';
  }

  handleMouseMove(e) {
    if (!this.isDragging) return;
    this.currentX = e.clientX;
  }

  handleMouseUp(e) {
    if (!this.isDragging) return;

    const diff = this.startX - this.currentX;
    const threshold = 50;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        this.next();
      } else {
        this.prev();
      }
    }

    this.isDragging = false;
    this.track.style.cursor = 'grab';
  }

  handleMouseLeave(e) {
    this.isDragging = false;
    this.track.style.cursor = 'grab';
  }

  setupKeyboard() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.prev();
      } else if (e.key === 'ArrowRight') {
        this.next();
      }
    });
  }

  setupImageModal() {
    this.slides.forEach((slide) => {
      const img = slide.querySelector('img');
      if (img && !img.hasAttribute('data-modal-initialized')) {
        img.setAttribute('data-modal-initialized', 'true');
        img.addEventListener('click', () => {
          const existingModal = document.querySelector('.image-modal');
          if (existingModal) {
            document.body.removeChild(existingModal);
          }
          this.openImageModal(img.src, img.alt);
        });
      }
    });
  }

  openImageModal(src, alt) {
    const existingModal = document.querySelector('.image-modal');
    if (existingModal) {
      document.body.removeChild(existingModal);
    }

    document.body.style.overflow = 'hidden';

    const modal = document.createElement('div');
    modal.className =
      'image-modal fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4';
    modal.innerHTML = `
      <div class="relative max-w-4xl max-h-full">
        <img src="${src}" alt="${alt}" class="w-full h-auto object-contain">
        <button class="close-modal absolute top-4 right-4 bg-white/20 hover:bg-white/40 text-white p-3 rounded-full transition-all duration-300">
          ✕
        </button>
      </div>
    `;

    const closeModal = () => {
      if (modal.parentNode) {
        document.body.removeChild(modal);
        document.removeEventListener('keydown', handleEsc);
        document.body.style.overflow = '';
      }
    };

    modal.addEventListener('click', (e) => {
      if (e.target === modal || e.target.classList.contains('close-modal')) {
        closeModal();
      }
    });

    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };
    document.addEventListener('keydown', handleEsc);

    document.body.appendChild(modal);
  }

  goToSlide(index) {
    if (index < 0 || index >= this.totalSlides) return;

    this.currentSlide = index;

    const isMobile = window.innerWidth < 1024;
    const slideWidth = isMobile ? 320 : 651;
    const gap = isMobile ? 6 : 24;

    const translateX = -(index * (slideWidth + gap));

    this.list.style.transform = `translateX(${translateX}px)`;

    this.updateActiveSlides();
    this.updateArrows();
  }

  next() {
    const nextIndex = this.currentSlide + 1;
    if (nextIndex < this.totalSlides) {
      this.goToSlide(nextIndex);
    }
  }

  prev() {
    const prevIndex = this.currentSlide - 1;
    if (prevIndex >= 0) {
      this.goToSlide(prevIndex);
    }
  }

  updateArrows() {
    if (!this.options.arrows) return;

    const isMobile = window.innerWidth < 1024;
    if (isMobile) return;

    const prevBtn = this.container.querySelector('.splide__arrow--prev');
    const nextBtn = this.container.querySelector('.splide__arrow--next');

    if (prevBtn) {
      prevBtn.style.opacity = this.currentSlide === 0 ? '0.5' : '1';
      prevBtn.style.pointerEvents = this.currentSlide === 0 ? 'none' : 'auto';
    }

    if (nextBtn) {
      const maxSlide = this.totalSlides - 2;
      nextBtn.style.opacity = this.currentSlide >= maxSlide ? '0.5' : '1';
      nextBtn.style.pointerEvents =
        this.currentSlide >= maxSlide ? 'none' : 'auto';
    }
  }

  updatePagination() {
    const pagination = this.container.querySelector('.splide__pagination');
    if (!pagination) return;

    const pages = pagination.querySelectorAll('.splide__pagination__page');
    pages.forEach((page, index) => {
      const isActive =
        index === Math.floor(this.currentSlide / this.options.perPage);
      page.classList.toggle('is-active', isActive);
    });
  }

  startAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
    }

    this.autoplayInterval = setInterval(() => {
      if (this.currentSlide >= this.totalSlides - 2) {
        this.goToSlide(0);
      } else {
        this.next();
      }
    }, this.options.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  updateActiveSlides() {
    this.slides.forEach((slide, i) => {
      slide.classList.remove('active');
    });

    const startIndex = this.currentSlide;
    const endIndex = Math.min(startIndex + 1, this.totalSlides - 1);

    for (let i = startIndex; i <= endIndex; i++) {
      if (this.slides[i]) {
        this.slides[i].classList.add('active');
      }
    }
  }

  destroy() {
    this.stopAutoplay();
  }
}

document.addEventListener('DOMContentLoaded', function () {
  const sliders = document.querySelectorAll('.splide-gallery');

  sliders.forEach((slider, index) => {
    new SimpleSlider(slider, {
      perPage: 2,
      perMove: 1,
      gap: '2rem',
      autoplay: false,
      autoplaySpeed: 4000,
      loop: false,
      pagination: false,
      arrows: true,
    });
  });
});

function openImageModal(imageSrc, imageAlt) {
  const modal = document.getElementById('imageModal');
  const modalImage = document.getElementById('modalImage');

  if (modal && modalImage) {
    modalImage.src = imageSrc;
    modalImage.alt = imageAlt;
    modal.classList.remove('hidden');
    modal.classList.add('flex');
    document.body.style.overflow = 'hidden';
  }
}

function closeImageModal() {
  const modal = document.getElementById('imageModal');

  if (modal) {
    modal.classList.add('hidden');
    modal.classList.remove('flex');
    document.body.style.overflow = 'auto';
  }
}

document.addEventListener('DOMContentLoaded', function () {
  const modal = document.getElementById('imageModal');
  if (modal) {
    modal.addEventListener('click', function (e) {
      if (e.target === this) {
        closeImageModal();
      }
    });
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      closeImageModal();
    }
  });
});